/**
 * Pure view model and copy for the Settings "Pool" card's name field.
 *
 * This module is pure: no React import, no DOM, no fetch. It holds the one
 * rule the field has, which is the same rule `postPoolUpdateHandler` applies
 * to `body.name`: trimmed, not empty, at most 80 characters. Stated here so a
 * fan who pastes a paragraph is told before the request rather than by a
 * refusal afterwards, and stated as one function so the button's disabled
 * state and the inline message can never disagree about it.
 *
 * The server still validates. This is the courtesy, not the enforcement.
 */

export const POOL_NAME_LABEL = "Pool name";
export const POOL_NAME_HINT = "Everyone in the pool sees this name.";
export const POOL_NAME_SAVE_LABEL = "Save name";
export const POOL_NAME_MAX_LENGTH = 80;

export const POOL_NAME_EMPTY_ERROR = "Give the pool a name.";
export const POOL_NAME_TOO_LONG_ERROR = `Keep the name to ${POOL_NAME_MAX_LENGTH} characters or fewer.`;

export type typePoolNameFieldState = {
  /** The value to send. Null whenever there is nothing worth sending. */
  nextName: string | null;
  /** Inline message under the field. Null when the value is fine. */
  error: string | null;
  isSaveEnabled: boolean;
};

export function resolvePoolNameFieldState(input: {
  draft: string;
  /** The saved name from `POST /pool/get`, or null while it loads. */
  savedName: string | null;
}): typePoolNameFieldState {
  const trimmed = input.draft.trim();

  // An empty box is the starting state of a field a fan has cleared to retype,
  // so it disables the button and says what is missing rather than shouting
  // before they have typed anything back.
  if (trimmed.length === 0) {
    return {
      nextName: null,
      error: input.draft.length === 0 ? null : POOL_NAME_EMPTY_ERROR,
      isSaveEnabled: false,
    };
  }
  if (trimmed.length > POOL_NAME_MAX_LENGTH) {
    return {
      nextName: null,
      error: POOL_NAME_TOO_LONG_ERROR,
      isSaveEnabled: false,
    };
  }
  // Saving the name it already has is a write that changes nothing and an
  // audit event that says nothing, so the button stays off until the value
  // actually differs. Compared against the trimmed draft because that is what
  // would be sent.
  if (input.savedName !== null && trimmed === input.savedName) {
    return { nextName: null, error: null, isSaveEnabled: false };
  }
  return { nextName: trimmed, error: null, isSaveEnabled: true };
}
